import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, type Subscription } from '@/lib/dataProvider'
import { logger } from '@/lib/logger'

export interface PlaylistTrack {
  nodeId: string
  name: string
  url: string
  volume: number
}

export interface AudioCommand {
  type: 'play-music' | 'stop-music' | 'stop-all' | 'set-volume' | 'set-master-volume' | 'playlist-next' | 'playlist-prev' | 'playlist-stop' | 'playlist-pause' | 'playlist-resume' | 'playlist-shuffle' | 'playlist-repeat' | 'play-playlist-track'
  nodeId?: string
  name?: string
  url?: string
  volume?: number
  loop?: boolean
  // Master volume for entire display (0-1)
  masterVolume?: number
  // Playlist info
  playlistId?: string
  playlistName?: string
  trackIndex?: number
  totalTracks?: number
  shuffle?: boolean
  repeatMode?: 'none' | 'all' | 'one'
  // Full playlist tracks (for prev/next to work)
  tracks?: PlaylistTrack[]
}

export interface PlayingMusicItem {
  nodeId: string
  name: string
  volume: number
  loop: boolean
  audio: HTMLAudioElement
  currentTime?: number
  duration?: number
  // Playlist context
  playlistId?: string
  playlistName?: string
  trackIndex?: number
  totalTracks?: number
  shuffle?: boolean
  repeatMode?: 'none' | 'all' | 'one'
}

export interface DisplayMusicInfo {
  nodeId: string
  name: string
  volume: number
  loop: boolean
  displayId: string
  currentTime?: number
  duration?: number
  // Playlist context
  playlistId?: string
  playlistName?: string
  trackIndex?: number
  totalTracks?: number
  shuffle?: boolean
  repeatMode?: 'none' | 'all' | 'one'
}

export interface DisplayPlayState {
  displayId: string
  isPaused?: boolean
  masterVolume?: number
  items: Array<{
    nodeId: string
    name: string
    volume: number
    loop: boolean
    currentTime?: number
    duration?: number
    playlistId?: string
    playlistName?: string
    trackIndex?: number
    totalTracks?: number
    shuffle?: boolean
    repeatMode?: 'none' | 'all' | 'one'
  }>
}

export const useDisplayAudioStore = defineStore('displayAudio', () => {
  // ============================================
  // LOCAL PLAYBACK STATE (for presenter views)
  // ============================================
  const playingMusic = ref<Map<string, PlayingMusicItem>>(new Map())
  const isMuted = ref(false)
  const previousVolumes = ref<Map<string, number>>(new Map())
  const currentDisplayId = ref<string | null>(null)
  const isPaused = ref(false) // Track global pause state for playlists
  const masterVolume = ref(1) // Master volume for the display (0-1)

  // Active playlist state for presenter
  interface ActivePlaylistState {
    playlistId: string
    playlistName: string
    tracks: PlaylistTrack[] // All tracks with their info
    currentIndex: number
    shuffle: boolean
    shuffleOrder: number[] // Shuffled indices (or normal order if not shuffled)
    repeatMode: 'none' | 'all' | 'one'
  }
  const activePlaylist = ref<ActivePlaylistState | null>(null)

  // Get currently playing music as array
  const playingMusicList = computed(() => Array.from(playingMusic.value.values()))

  // ============================================
  // EDITOR STATE (tracks what displays are playing)
  // ============================================
  const displayPlayStates = ref<Map<string, DisplayPlayState>>(new Map())
  const editorSubscriptions = ref<Map<string, Subscription>>(new Map())

  // Get all display music items for the sidebar
  const allDisplayMusic = computed((): DisplayMusicInfo[] => {
    const result: DisplayMusicInfo[] = []
    displayPlayStates.value.forEach((state) => {
      state.items.forEach((item) => {
        result.push({
          ...item,
          displayId: state.displayId
        })
      })
    })
    return result
  })

  // Get music grouped by display
  const musicByDisplay = computed(() => {
    const result = new Map<string, DisplayMusicInfo[]>()
    displayPlayStates.value.forEach((state) => {
      const items = state.items.map(item => ({
        ...item,
        displayId: state.displayId
      }))
      if (items.length > 0) {
        result.set(state.displayId, items)
      }
    })
    return result
  })

  // Get playing items for a specific display
  const getPlayingItemsForDisplay = (displayId: string) => {
    const state = displayPlayStates.value.get(displayId)
    return state?.items || []
  }

  // ============================================
  // PRESENTER BROADCAST CHANNEL
  // ============================================
  const presenterSubscription = ref<Subscription | null>(null)
  let reportingInterval: ReturnType<typeof setInterval> | null = null

  // Subscribe to manual audio commands from the editor (PRESENTER)
  const subscribeAsPresenter = async (
    displayId: string,
    onCommand: (command: AudioCommand) => void
  ): Promise<Subscription> => {
    currentDisplayId.value = displayId
    const channelName = `display-audio:${displayId}`
    
    const provider = getDataProvider()
    
    const subscription = provider.subscribeToChannel(channelName, {
      'audio-command': (payload: AudioCommand) => {
        logger.info(`📥 Received audio command for display ${displayId}:`, payload)
        onCommand(payload)
      }
    })
    
    presenterSubscription.value = subscription
    
    // Start reporting play state every 2 seconds
    reportingInterval = setInterval(() => {
      broadcastPlayState()
    }, 2000)
    // Also report immediately
    broadcastPlayState()
    
    logger.info(`🎧 Subscribed as presenter for display ${displayId}`)
    return subscription
  }

  // Unsubscribe presenter
  const unsubscribeAsPresenter = async () => {
    if (reportingInterval) {
      clearInterval(reportingInterval)
      reportingInterval = null
    }
    if (presenterSubscription.value) {
      presenterSubscription.value.unsubscribe()
      presenterSubscription.value = null
    }
    currentDisplayId.value = null
  }

  // Broadcast current play state to editor (PRESENTER)
  const broadcastPlayState = async () => {
    if (!currentDisplayId.value) return
    
    const state: DisplayPlayState = {
      displayId: currentDisplayId.value,
      isPaused: isPaused.value,
      masterVolume: masterVolume.value,
      items: playingMusicList.value.map(item => ({
        nodeId: item.nodeId,
        name: item.name,
        volume: item.volume,
        loop: item.loop,
        currentTime: item.audio?.currentTime || 0,
        duration: item.audio?.duration || 0,
        playlistId: item.playlistId,
        playlistName: item.playlistName,
        trackIndex: item.trackIndex,
        totalTracks: item.totalTracks,
        shuffle: item.shuffle,
        repeatMode: item.repeatMode
      }))
    }
    
    const provider = getDataProvider()
    const channelName = `display-audio:${currentDisplayId.value}`
    await provider.broadcast(channelName, 'play-state', state)
    
    logger.debug(`📤 Broadcast play state: ${state.items.length} items`)
  }

  // ============================================
  // EDITOR SUBSCRIPTION (to receive play states)
  // ============================================
  
  // Subscribe to a display's play state (EDITOR)
  const subscribeToDisplay = async (displayId: string): Promise<void> => {
    if (editorSubscriptions.value.has(displayId)) {
      console.log(`🔊 Already subscribed to display ${displayId}`)
      return
    }
    
    const channelName = `display-audio:${displayId}`
    console.log(`🔊 Editor creating channel: ${channelName}`)
    
    const provider = getDataProvider()
    
    const subscription = provider.subscribeToChannel(channelName, {
      'play-state': (payload: DisplayPlayState) => {
        const state = payload
        displayPlayStates.value.set(displayId, state)
        // Trigger reactivity
        displayPlayStates.value = new Map(displayPlayStates.value)
      }
    })
    
    console.log(`✅ Editor subscribed to channel: ${channelName}`)
    editorSubscriptions.value.set(displayId, subscription)
  }

  // Unsubscribe from a display (EDITOR)
  const unsubscribeFromDisplay = async (displayId: string): Promise<void> => {
    const subscription = editorSubscriptions.value.get(displayId)
    if (subscription) {
      subscription.unsubscribe()
      editorSubscriptions.value.delete(displayId)
      displayPlayStates.value.delete(displayId)
    }
  }

  // Unsubscribe from all displays (EDITOR)
  const unsubscribeFromAllDisplays = async (): Promise<void> => {
    for (const [displayId] of editorSubscriptions.value) {
      await unsubscribeFromDisplay(displayId)
    }
  }

  // Send a command to a display (EDITOR)
  const sendCommandToDisplay = async (displayId: string, command: AudioCommand): Promise<void> => {
    // Subscribe first if not already
    if (!editorSubscriptions.value.has(displayId)) {
      await subscribeToDisplay(displayId)
    }
    
    const channelName = `display-audio:${displayId}`
    const provider = getDataProvider()
    
    logger.info(`📤 Sending command to display ${displayId}:`, command)
    await provider.broadcast(channelName, 'audio-command', command)
  }

  // ============================================
  // LOCAL PLAYBACK FUNCTIONS (for presenter)
  // ============================================

  // Playlist info type for passing to playMusicLocally
  interface PlaylistContext {
    playlistId: string
    playlistName?: string
    trackIndex?: number
    totalTracks?: number
    shuffle?: boolean
    repeatMode?: 'none' | 'all' | 'one'
  }

  // Play music locally - keyed by nodeId to prevent duplicates
  const playMusicLocally = async (
    nodeId: string, 
    name: string, 
    url: string, 
    volume: number = 1, 
    loop: boolean = true,
    playlistContext?: PlaylistContext
  ): Promise<boolean> => {
    // If already playing this nodeId, just restart it
    const existing = playingMusic.value.get(nodeId)
    if (existing) {
      logger.info('🔄 Restarting already-playing audio:', name)
      existing.audio.currentTime = 0
      existing.audio.volume = isMuted.value ? 0 : volume * masterVolume.value
      existing.audio.loop = loop
      // Update playlist context if provided
      if (playlistContext) {
        existing.playlistId = playlistContext.playlistId
        existing.playlistName = playlistContext.playlistName
        existing.trackIndex = playlistContext.trackIndex
        existing.totalTracks = playlistContext.totalTracks
        existing.shuffle = playlistContext.shuffle
        existing.repeatMode = playlistContext.repeatMode
        playingMusic.value = new Map(playingMusic.value)
      }
      if (existing.audio.paused) {
        await existing.audio.play()
      }
      return true
    }

    try {
      const audio = new Audio(url)
      audio.volume = isMuted.value ? 0 : volume * masterVolume.value
      audio.loop = loop

      audio.onended = () => {
        if (!audio.loop) {
          // Check if we're in a playlist and should auto-advance
          if (activePlaylist.value) {
            const playlist = activePlaylist.value
            let newIndex = playlist.currentIndex + 1
            
            if (newIndex >= playlist.tracks.length) {
              if (playlist.repeatMode === 'all') {
                // Loop back to start
                newIndex = 0
                // Re-shuffle if shuffle is on
                if (playlist.shuffle) {
                  playlist.shuffleOrder = generateShuffleOrder(playlist.tracks.length)
                }
                // Continue to play next track
                playingMusic.value.delete(nodeId)
                playingMusic.value = new Map(playingMusic.value)
                playPlaylistTrack(newIndex)
              } else {
                // Playlist ended, clear state
                playingMusic.value.delete(nodeId)
                playingMusic.value = new Map(playingMusic.value)
                activePlaylist.value = null
                broadcastPlayState()
              }
            } else {
              // Play next track
              playingMusic.value.delete(nodeId)
              playingMusic.value = new Map(playingMusic.value)
              playPlaylistTrack(newIndex)
            }
          } else {
            // Not a playlist, just remove from playing
            playingMusic.value.delete(nodeId)
            playingMusic.value = new Map(playingMusic.value)
          }
        }
      }

      audio.onerror = (e) => {
        logger.error('❌ Audio error for', name, ':', e)
        playingMusic.value.delete(nodeId)
        playingMusic.value = new Map(playingMusic.value)
      }

      await audio.play()

      playingMusic.value.set(nodeId, {
        nodeId,
        name,
        volume,
        loop,
        audio,
        // Include playlist context if provided
        ...(playlistContext && {
          playlistId: playlistContext.playlistId,
          playlistName: playlistContext.playlistName,
          trackIndex: playlistContext.trackIndex,
          totalTracks: playlistContext.totalTracks,
          shuffle: playlistContext.shuffle,
          repeatMode: playlistContext.repeatMode
        })
      })
      // Trigger reactivity
      playingMusic.value = new Map(playingMusic.value)

      logger.info('▶️ Playing Music locally:', name, '(nodeId:', nodeId, ')', playlistContext ? `[Playlist: ${playlistContext.playlistName}]` : '')
      return true
    } catch (error) {
      logger.error('❌ Failed to play audio locally:', error)
      return false
    }
  }

  // Fade out duration in milliseconds
  const FADE_OUT_DURATION = 5000
  const FADE_IN_DURATION = 1000  // Shorter fade in for responsiveness
  const CROSSFADE_DURATION = 2000  // Duration for crossfade between tracks
  const FADE_STEP_INTERVAL = 50 // Update volume every 50ms

  // Fade out audio element over duration
  const fadeOutAudio = (audio: HTMLAudioElement, duration: number = FADE_OUT_DURATION): Promise<void> => {
    return new Promise((resolve) => {
      const startVolume = audio.volume
      const steps = duration / FADE_STEP_INTERVAL
      const volumeStep = startVolume / steps
      let currentStep = 0

      const fadeInterval = setInterval(() => {
        currentStep++
        const newVolume = Math.max(0, startVolume - (volumeStep * currentStep))
        audio.volume = newVolume

        if (currentStep >= steps || newVolume <= 0) {
          clearInterval(fadeInterval)
          audio.pause()
          audio.currentTime = 0
          audio.volume = startVolume // Reset volume for potential reuse
          resolve()
        }
      }, FADE_STEP_INTERVAL)
    })
  }

  // Fade in audio element over duration
  const fadeInAudio = (audio: HTMLAudioElement, targetVolume: number, duration: number = FADE_IN_DURATION): Promise<void> => {
    return new Promise((resolve) => {
      audio.volume = 0
      const steps = duration / FADE_STEP_INTERVAL
      const volumeStep = targetVolume / steps
      let currentStep = 0

      const fadeInterval = setInterval(() => {
        currentStep++
        const newVolume = Math.min(targetVolume, volumeStep * currentStep)
        audio.volume = newVolume

        if (currentStep >= steps || newVolume >= targetVolume) {
          clearInterval(fadeInterval)
          audio.volume = targetVolume
          resolve()
        }
      }, FADE_STEP_INTERVAL)
    })
  }

  // Play music with fade in - keyed by nodeId to prevent duplicates
  const playMusicWithFadeIn = async (
    nodeId: string, 
    name: string, 
    url: string, 
    volume: number = 1, 
    loop: boolean = true,
    playlistContext?: PlaylistContext,
    fadeDuration: number = FADE_IN_DURATION
  ): Promise<boolean> => {
    try {
      const audio = new Audio(url)
      const targetVolume = isMuted.value ? 0 : volume * masterVolume.value
      audio.volume = 0 // Start at 0 for fade in
      audio.loop = loop

      audio.onended = () => {
        if (!audio.loop) {
          // Check if we're in a playlist and should auto-advance
          if (activePlaylist.value) {
            const playlist = activePlaylist.value
            let newIndex = playlist.currentIndex + 1
            
            if (newIndex >= playlist.tracks.length) {
              if (playlist.repeatMode === 'all') {
                newIndex = 0
                if (playlist.shuffle) {
                  playlist.shuffleOrder = generateShuffleOrder(playlist.tracks.length)
                }
                playingMusic.value.delete(nodeId)
                playingMusic.value = new Map(playingMusic.value)
                playPlaylistTrack(newIndex)
              } else {
                playingMusic.value.delete(nodeId)
                playingMusic.value = new Map(playingMusic.value)
                activePlaylist.value = null
                broadcastPlayState()
              }
            } else {
              playingMusic.value.delete(nodeId)
              playingMusic.value = new Map(playingMusic.value)
              playPlaylistTrack(newIndex)
            }
          } else {
            playingMusic.value.delete(nodeId)
            playingMusic.value = new Map(playingMusic.value)
          }
        }
      }

      audio.onerror = (e) => {
        logger.error('❌ Audio error for', name, ':', e)
        playingMusic.value.delete(nodeId)
        playingMusic.value = new Map(playingMusic.value)
      }

      await audio.play()

      playingMusic.value.set(nodeId, {
        nodeId,
        name,
        volume,
        loop,
        audio,
        ...(playlistContext && {
          playlistId: playlistContext.playlistId,
          playlistName: playlistContext.playlistName,
          trackIndex: playlistContext.trackIndex,
          totalTracks: playlistContext.totalTracks,
          shuffle: playlistContext.shuffle,
          repeatMode: playlistContext.repeatMode
        })
      })
      playingMusic.value = new Map(playingMusic.value)

      // Fade in the audio
      fadeInAudio(audio, targetVolume, fadeDuration)

      logger.info('▶️ Playing Music with fade-in:', name, '(nodeId:', nodeId, ')', playlistContext ? `[Playlist: ${playlistContext.playlistName}]` : '')
      return true
    } catch (error) {
      logger.error('❌ Failed to play audio locally:', error)
      return false
    }
  }

  // Crossfade between current track and new track
  const crossfadeToTrack = async (
    nodeId: string,
    name: string,
    url: string,
    volume: number = 1,
    loop: boolean = true,
    playlistContext?: PlaylistContext
  ): Promise<boolean> => {
    // Get currently playing track in the same playlist
    const currentItems = Array.from(playingMusic.value.values()).filter(
      item => playlistContext?.playlistId && item.playlistId === playlistContext.playlistId
    )

    // If the clicked track is already playing, just toggle it off
    const clickedTrackPlaying = currentItems.find(item => item.nodeId === nodeId)
    if (clickedTrackPlaying) {
      await stopMusicLocally(nodeId, true)
      activePlaylist.value = null
      return true
    }

    // Start fading out current tracks while starting the new one
    const fadeOutPromises = currentItems.map(item => {
      playingMusic.value.delete(item.nodeId)
      return fadeOutAudio(item.audio, CROSSFADE_DURATION)
    })
    playingMusic.value = new Map(playingMusic.value)

    // Start the new track with fade in (parallel with fade out)
    const playResult = await playMusicWithFadeIn(
      nodeId,
      name,
      url,
      volume,
      loop,
      playlistContext,
      CROSSFADE_DURATION
    )

    // Wait for fade outs to complete
    await Promise.all(fadeOutPromises)

    return playResult
  }

  // Stop specific music by nodeId with fade out
  const stopMusicLocally = async (nodeId: string, fadeOut: boolean = true): Promise<void> => {
    const item = playingMusic.value.get(nodeId)
    if (item) {
      logger.info('⏹️ Stopping Music locally (fade:', fadeOut, '):', item.name)
      
      // Remove from map immediately so UI updates
      playingMusic.value.delete(nodeId)
      playingMusic.value = new Map(playingMusic.value)
      
      if (fadeOut) {
        await fadeOutAudio(item.audio)
      } else {
        item.audio.pause()
        item.audio.currentTime = 0
      }
      
      logger.info('⏹️ Stopped Music locally:', item.name)
    }
  }

  // Stop all music locally with fade out
  const stopAllMusicLocally = async (fadeOut: boolean = true): Promise<void> => {
    const items = Array.from(playingMusic.value.values())
    
    // Clear map immediately so UI updates
    playingMusic.value.clear()
    playingMusic.value = new Map(playingMusic.value)
    
    if (fadeOut && items.length > 0) {
      // Fade out all items in parallel
      await Promise.all(items.map(item => fadeOutAudio(item.audio)))
    } else {
      // Stop immediately
      items.forEach(item => {
        item.audio.pause()
        item.audio.currentTime = 0
      })
    }
    
    logger.info('⏹️ Stopped all music locally')
  }

  // Set volume for specific music (applies master volume multiplier)
  const setVolumeLocally = (nodeId: string, volume: number) => {
    const item = playingMusic.value.get(nodeId)
    if (item) {
      item.volume = volume
      if (!isMuted.value) {
        item.audio.volume = volume * masterVolume.value
      }
    }
  }

  // Set master volume for entire display
  const setMasterVolumeLocally = (volume: number) => {
    masterVolume.value = Math.max(0, Math.min(1, volume))
    
    // Apply to all currently playing audio
    playingMusic.value.forEach((item) => {
      if (!isMuted.value) {
        item.audio.volume = item.volume * masterVolume.value
      }
    })
    
    logger.info(`🎚️ Master volume set to ${Math.round(masterVolume.value * 100)}%`)
  }

  // Toggle mute
  const toggleMuteLocally = () => {
    isMuted.value = !isMuted.value
    
    playingMusic.value.forEach((item) => {
      if (isMuted.value) {
        previousVolumes.value.set(item.nodeId, item.audio.volume)
        item.audio.volume = 0
      } else {
        const vol = previousVolumes.value.get(item.nodeId) || item.volume
        item.audio.volume = vol * masterVolume.value
      }
    })

    logger.info(isMuted.value ? '🔇 Audio muted locally' : '🔊 Audio unmuted locally')
  }

  // Helper to generate shuffle order
  const generateShuffleOrder = (length: number, startIndex = 0): number[] => {
    const order = Array.from({ length }, (_, i) => i)
    // Fisher-Yates shuffle
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[order[i], order[j]] = [order[j], order[i]]
    }
    // Move the starting index to the front if provided
    if (startIndex > 0) {
      const idx = order.indexOf(startIndex)
      if (idx > 0) {
        order.splice(idx, 1)
        order.unshift(startIndex)
      }
    }
    return order
  }

  // Helper to play a specific track from the active playlist (with crossfade)
  const playPlaylistTrack = async (index: number) => {
    if (!activePlaylist.value) return
    
    const playlist = activePlaylist.value
    const shuffledIndex = playlist.shuffleOrder[index]
    const track = playlist.tracks[shuffledIndex]
    
    if (!track) {
      logger.warn('Track not found at index:', index)
      return
    }

    // Update active playlist index
    playlist.currentIndex = index
    
    // Build playlist context
    const playlistContext = {
      playlistId: playlist.playlistId,
      playlistName: playlist.playlistName,
      trackIndex: shuffledIndex,
      totalTracks: playlist.tracks.length,
      shuffle: playlist.shuffle,
      repeatMode: playlist.repeatMode
    }
    
    // Use crossfade to switch tracks smoothly
    await crossfadeToTrack(
      track.nodeId, 
      track.name, 
      track.url, 
      track.volume, 
      playlist.repeatMode === 'one',
      playlistContext
    )
    
    isPaused.value = false
    broadcastPlayState()
    
    logger.info(`Playing playlist track ${index + 1}/${playlist.tracks.length}: ${track.name}`)
  }

  // Handle a command from the editor (PRESENTER)
  const handleCommand = async (command: AudioCommand) => {
    switch (command.type) {
      case 'play-music':
        if (command.nodeId && command.name && command.url) {
          // Build playlist context if this is a playlist track
          const playlistContext = command.playlistId ? {
            playlistId: command.playlistId,
            playlistName: command.playlistName,
            trackIndex: command.trackIndex,
            totalTracks: command.totalTracks,
            shuffle: command.shuffle,
            repeatMode: command.repeatMode
          } : undefined
          
          await playMusicLocally(
            command.nodeId, 
            command.name, 
            command.url, 
            command.volume || 1, 
            command.loop !== false,
            playlistContext
          )
          
          // If this is a playlist, set up playlist state for prev/next navigation
          if (command.playlistId && command.tracks) {
            const order = command.shuffle 
              ? generateShuffleOrder(command.tracks.length, command.trackIndex ?? 0)
              : Array.from({ length: command.tracks.length }, (_, i) => i)
            
            activePlaylist.value = {
              playlistId: command.playlistId,
              playlistName: command.playlistName || '',
              tracks: command.tracks,
              currentIndex: command.shuffle ? 0 : (command.trackIndex ?? 0),
              shuffle: command.shuffle ?? false,
              shuffleOrder: order,
              repeatMode: command.repeatMode ?? 'none'
            }
            
            logger.info(`📻 Playlist "${command.playlistName}" loaded with ${command.tracks.length} tracks`)
          }
          
          // Reset pause state when starting playback
          isPaused.value = false
          // Immediately broadcast updated state
          broadcastPlayState()
          broadcastPlayState()
        }
        break
      case 'stop-music':
        if (command.nodeId) {
          await stopMusicLocally(command.nodeId)
          // Clear active playlist if stopping the currently playing playlist track
          if (activePlaylist.value) {
            const currentTrack = activePlaylist.value.tracks[activePlaylist.value.shuffleOrder[activePlaylist.value.currentIndex]]
            if (currentTrack && currentTrack.nodeId === command.nodeId) {
              activePlaylist.value = null
            }
          }
          // Immediately broadcast updated state
          broadcastPlayState()
        }
        break
      case 'stop-all':
        await stopAllMusicLocally()
        activePlaylist.value = null
        // Immediately broadcast updated state
        broadcastPlayState()
        break
      case 'set-volume':
        if (command.nodeId && command.volume !== undefined) {
          setVolumeLocally(command.nodeId, command.volume)
          // Immediately broadcast updated state
          broadcastPlayState()
        }
        break
      case 'set-master-volume':
        if (command.masterVolume !== undefined) {
          setMasterVolumeLocally(command.masterVolume)
          // Immediately broadcast updated state
          broadcastPlayState()
        }
        break
      case 'playlist-pause':
        // Pause all playing audio
        isPaused.value = true
        playingMusic.value.forEach(item => {
          if (item.audio && !item.audio.paused) {
            item.audio.pause()
          }
        })
        broadcastPlayState()
        break
      case 'playlist-resume':
        // Resume all paused audio
        isPaused.value = false
        playingMusic.value.forEach(item => {
          if (item.audio && item.audio.paused) {
            item.audio.play()
          }
        })
        broadcastPlayState()
        break
      case 'playlist-prev':
        if (activePlaylist.value) {
          const playlist = activePlaylist.value
          let newIndex = playlist.currentIndex - 1
          
          if (newIndex < 0) {
            if (playlist.repeatMode === 'all') {
              newIndex = playlist.tracks.length - 1
            } else {
              // At the beginning, do nothing or restart current track
              const item = playingMusic.value.values().next().value
              if (item?.audio) item.audio.currentTime = 0
              return
            }
          }
          
          await playPlaylistTrack(newIndex)
        }
        break
      case 'playlist-next':
        if (activePlaylist.value) {
          const playlist = activePlaylist.value
          let newIndex = playlist.currentIndex + 1
          
          if (newIndex >= playlist.tracks.length) {
            if (playlist.repeatMode === 'all') {
              newIndex = 0
              // Re-shuffle if shuffle is on
              if (playlist.shuffle) {
                playlist.shuffleOrder = generateShuffleOrder(playlist.tracks.length)
              }
            } else {
              // At the end, stop
              await stopAllMusicLocally()
              activePlaylist.value = null
              broadcastPlayState()
              return
            }
          }
          
          await playPlaylistTrack(newIndex)
        }
        break
      case 'playlist-shuffle':
        if (activePlaylist.value) {
          activePlaylist.value.shuffle = command.shuffle ?? false
          // Regenerate shuffle order if turning shuffle on
          if (command.shuffle) {
            activePlaylist.value.shuffleOrder = generateShuffleOrder(
              activePlaylist.value.tracks.length,
              activePlaylist.value.shuffleOrder[activePlaylist.value.currentIndex]
            )
            // Reset to start of new shuffle order
            activePlaylist.value.currentIndex = 0
          } else {
            // Turn shuffle off - restore natural order
            activePlaylist.value.shuffleOrder = Array.from(
              { length: activePlaylist.value.tracks.length }, 
              (_, i) => i
            )
            // Find current track in natural order
            const currentItem = playingMusic.value.values().next().value
            if (currentItem) {
              const naturalIndex = activePlaylist.value.tracks.findIndex(t => t.nodeId === currentItem.nodeId)
              if (naturalIndex >= 0) {
                activePlaylist.value.currentIndex = naturalIndex
              }
            }
          }
        }
        // Update shuffle state on all playing items
        playingMusic.value.forEach(item => {
          if (item.playlistId) {
            item.shuffle = command.shuffle ?? false
          }
        })
        playingMusic.value = new Map(playingMusic.value)
        broadcastPlayState()
        break
      case 'playlist-repeat':
        if (activePlaylist.value) {
          activePlaylist.value.repeatMode = command.repeatMode ?? 'none'
        }
        // Update repeat mode on all playing items
        playingMusic.value.forEach(item => {
          if (item.playlistId) {
            item.repeatMode = command.repeatMode ?? 'none'
            // Update loop on audio element
            if (item.audio) {
              item.audio.loop = command.repeatMode === 'one'
            }
          }
        })
        playingMusic.value = new Map(playingMusic.value)
        broadcastPlayState()
        break
      case 'play-playlist-track':
        // Play a specific track in a playlist with crossfade
        if (command.nodeId && command.name && command.url && command.playlistId) {
          const playlistContext = {
            playlistId: command.playlistId,
            playlistName: command.playlistName,
            trackIndex: command.trackIndex,
            totalTracks: command.totalTracks,
            shuffle: command.shuffle,
            repeatMode: command.repeatMode
          }

          // Use crossfade to smoothly switch between tracks
          await crossfadeToTrack(
            command.nodeId,
            command.name,
            command.url,
            command.volume || 1,
            command.repeatMode === 'one',
            playlistContext
          )

          // Update or set up playlist state for prev/next navigation
          if (command.tracks) {
            const trackIndex = command.tracks.findIndex(t => t.nodeId === command.nodeId)
            const order = command.shuffle
              ? generateShuffleOrder(command.tracks.length, trackIndex >= 0 ? trackIndex : 0)
              : Array.from({ length: command.tracks.length }, (_, i) => i)

            activePlaylist.value = {
              playlistId: command.playlistId,
              playlistName: command.playlistName || '',
              tracks: command.tracks,
              currentIndex: command.shuffle ? 0 : (trackIndex >= 0 ? trackIndex : 0),
              shuffle: command.shuffle ?? false,
              shuffleOrder: order,
              repeatMode: command.repeatMode ?? 'none'
            }

            logger.info(`📻 Switched to track "${command.name}" in playlist "${command.playlistName}"`)
          }

          isPaused.value = false
          broadcastPlayState()
        }
        break
    }
  }

  // ============================================
  // CLEANUP
  // ============================================
  const cleanupPresenter = async () => {
    await unsubscribeAsPresenter()
    await stopAllMusicLocally(false) // No fade on cleanup
  }

  const cleanupEditor = async () => {
    await unsubscribeFromAllDisplays()
  }

  return {
    // State
    playingMusic,
    isMuted,
    currentDisplayId,
    
    // Constants
    FADE_OUT_DURATION,
    
    // Computed
    playingMusicList,
    
    // State
    masterVolume: computed(() => masterVolume.value),
    
    // Editor state
    displayPlayStates,
    allDisplayMusic,
    musicByDisplay,
    getPlayingItemsForDisplay,

    // Presenter functions
    subscribeAsPresenter,
    unsubscribeAsPresenter,
    broadcastPlayState,
    playMusicLocally,
    stopMusicLocally,
    stopAllMusicLocally,
    setVolumeLocally,
    setMasterVolumeLocally,
    toggleMuteLocally,
    handleCommand,
    cleanupPresenter,
    
    // Editor functions
    subscribeToDisplay,
    unsubscribeFromDisplay,
    unsubscribeFromAllDisplays,
    sendCommandToDisplay,
    cleanupEditor
  }
})
